/**
 * Quiz match — linked bundle lines in the cart.
 *
 * The "Find My Match" results page recommends the skin AND hair systems to
 * some customers. That offer is not a product: the buy box adds the two real
 * systems as two ordinary cart lines (snippets/bundle-buy-box.liquid →
 * assets/bundle-buy-box.js). The brief for it: "if one is removed, both are
 * removed" — and ONLY for lines added from the results page. A customer who
 * puts the same two systems in the cart from anywhere else keeps them
 * independent.
 *
 * Nothing in the theme links cart lines to each other, so this is a small
 * sitewide watcher rather than a change to the cart drawer (which is a webpack
 * build — see CLAUDE.md, "Webpack JS bundle drift"). It costs nothing until a
 * linked group exists.
 *
 * HOW A GROUP IS RECOGNISED. Every line the buy box adds carries two line
 * properties (bundle-buy-box.js, the payload builder): `_bundle_id`, one id
 * per add-to-cart, shared by the lines of that add; and `_froya_template`, the
 * template the add happened on (`page.<suffix>`). Lines whose template is the
 * results template and that share a `_bundle_id` are one group. The gift line
 * the buy box may add (`_nbbb_free_gift`) belongs to the group but does not
 * count as a member. A group is remembered (sessionStorage) once it has been
 * seen with two or more members — remembered as the VARIANTS of its members.
 *
 * WHEN IT ACTS. After any cart change the theme announces (`cart:refresh`,
 * `cart:updated`, `line-item-quantity:change:end` — all bubble to the
 * document), the cart is re-read. If a remembered group's variant is no longer
 * in the cart AT ALL while another of its variants still is, the rest of the
 * group is removed in one /cart/update.js call and a `cart:refresh` is
 * dispatched so the drawer and cart page re-render, the way the drawer's own
 * subscription toggle does.
 *
 * WHY PRESENCE IS JUDGED BY VARIANT, NOT BY THE TAG. The drawer's subscription
 * toggle removes a line and re-adds it (section-mini-cart.js,
 * _updateSubscription) with properties rebuilt from a short whitelist —
 * `_bundle_id` and `_froya_template` are not on it, so a toggled line comes
 * back untagged. Judged by tag it would look removed and take its partner
 * with it. Judged by variant it is still there. The price of that: when the
 * rule fires, an untagged line of a member variant (a toggled partner) is
 * removed together with the tagged ones. A same-variant line the customer
 * added independently would be indistinguishable from it — Shopify merges
 * identical lines anyway, so this is the accepted edge.
 *
 * Quantity changes leave the member set intact and are ignored. Memory is
 * sessionStorage, so a full-page cart (main-cart.liquid) reloading after a
 * removal is still caught by the page-load check.
 */
(function () {
  'use strict';

  var script = document.currentScript;
  var TEMPLATE = (script && script.getAttribute('data-template')) || 'page.quiz-match-results';
  var STORE_KEY = 'froya:quiz:match:bundle-groups';
  var EVENTS = ['cart:refresh', 'cart:updated', 'line-item-quantity:change:end'];
  var SETTLE_MS = 250;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var timer = null;
  var running = false;
  var rerun = false;

  /** { bundleId: [variantId, …] } — the members each remembered group had. */
  function readGroups() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function writeGroups(groups) {
    try {
      if (Object.keys(groups).length) {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(groups));
      } else {
        window.sessionStorage.removeItem(STORE_KEY);
      }
    } catch (e) {
      /* storage blocked — the page-load check is lost, live checks still work */
    }
  }

  function isLinked(item) {
    var p = item && item.properties;
    return !!(p && p._froya_template === TEMPLATE && p._bundle_id);
  }

  function reconcile(cart) {
    var items = cart && cart.items ? cart.items : [];
    var remembered = readGroups();
    var next = {};
    var removeKeys = [];

    var inCart = {};        // variantId -> true, every line
    var tagged = {};        // bundleId -> { members: [variantId…], keys: [lineKey…] }
    items.forEach(function (item) {
      var variant = String(item.variant_id);
      inCart[variant] = true;
      if (!isLinked(item)) return;
      var id = String(item.properties._bundle_id);
      var group = tagged[id] || (tagged[id] = { members: [], keys: [] });
      group.keys.push(item.key);
      if (item.properties._nbbb_free_gift !== 'true' && group.members.indexOf(variant) === -1) {
        group.members.push(variant);
      }
    });

    Object.keys(remembered).forEach(function (id) {
      var members = remembered[id];
      var present = members.filter(function (v) { return inCart[v]; });
      if (!present.length) return;                       // whole group gone — forget it
      if (present.length === members.length) {           // intact
        next[id] = members;
        return;
      }
      // A member is gone: remove the tagged remainder plus any untagged line of
      // a member variant (a subscription-toggled partner — see the header).
      var group = tagged[id];
      if (group) removeKeys = removeKeys.concat(group.keys);
      items.forEach(function (item) {
        if (isLinked(item)) return;
        if (item.properties && item.properties._bundle_id) return;
        if (members.indexOf(String(item.variant_id)) !== -1) removeKeys.push(item.key);
      });
    });

    Object.keys(tagged).forEach(function (id) {
      if (!next[id] && !remembered[id] && tagged[id].members.length >= 2) {
        next[id] = tagged[id].members;
      }
    });

    writeGroups(next);

    if (!removeKeys.length) return Promise.resolve();

    var updates = {};
    removeKeys.forEach(function (key) { updates[key] = 0; });
    return window.fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (updated) {
        if (!updated) return;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: updated, replacementDelay: 0 }
        }));
      });
  }

  function fetchCart() {
    return window.fetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) { return res.ok ? res.json() : null; });
  }

  function run(cartHint) {
    if (running) { rerun = true; return; }
    running = true;
    var source = cartHint ? Promise.resolve(cartHint) : fetchCart();
    source
      .then(function (cart) { return cart ? reconcile(cart) : null; })
      .catch(function () { /* network hiccup — the next cart event tries again */ })
      .then(function () {
        running = false;
        if (rerun) { rerun = false; run(null); }
      });
  }

  function onCartEvent(event) {
    var detail = event && event.detail;
    var cart = detail && detail.cart && Array.isArray(detail.cart.items) ? detail.cart : null;
    // Nothing remembered and no cart in the event: nothing to compare against,
    // and not worth a request in the sessions that never took the quiz.
    if (!cart && !Object.keys(readGroups()).length) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(function () { run(cart); }, SETTLE_MS);
  }

  EVENTS.forEach(function (name) {
    document.addEventListener(name, onCartEvent);
  });

  if (Object.keys(readGroups()).length) {
    run(null);
  }
})();
